import { useMemo } from 'react';
import { ApprovalStepStatus } from 'ui-product-approvals-common';
import type { ApprovalStep } from 'ui-product-approvals-common';

import { ACTOR } from '../constants/actors';

export interface UseApprovalStepsInfo {
  stepCount: number;
  pendingCount: number;
  approvedCount: number;
  stepStatusForCurrentUser: ApprovalStepStatus | undefined;
}

export const getApprovalStepsInfo = (
  approvalSteps: ApprovalStep[],
  currentUserId: string | undefined
) => {
  const defaultValue: UseApprovalStepsInfo = {
    stepCount: 0,
    pendingCount: 0,
    approvedCount: 0,
    stepStatusForCurrentUser: undefined,
  };
  if (!approvalSteps.length) {
    return defaultValue;
  }
  return approvalSteps.reduce((accumulator, approvalStep: ApprovalStep) => {
    const { pendingCount, approvedCount, stepStatusForCurrentUser } =
      accumulator;
    const { approvalStepStatus, approver } = approvalStep;
    const isPending =
      approvalStepStatus === ApprovalStepStatus.APPROVAL_PENDING;
    const isApproved = approvalStepStatus === ApprovalStepStatus.APPROVED;
    const isCurrentUsersStep =
      approver.actor.actorType === ACTOR.USER &&
      approver.actor.id === currentUserId;
    return {
      stepCount: approvalSteps.length,
      pendingCount: isPending ? pendingCount + 1 : pendingCount,
      approvedCount: isApproved ? approvedCount + 1 : approvedCount,
      stepStatusForCurrentUser: isCurrentUsersStep
        ? approvalStepStatus
        : stepStatusForCurrentUser,
    };
  }, defaultValue);
};

export const useApprovalStepsInfo = (
  approvalSteps: ApprovalStep[],
  currentUserId: string | undefined
): UseApprovalStepsInfo =>
  useMemo<UseApprovalStepsInfo>(
    () => getApprovalStepsInfo(approvalSteps, currentUserId),
    [approvalSteps, currentUserId]
  );
